// frontend/src/pages/admin/CreateUserPage.tsx
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { ArrowLeft, Save, Loader2, AlertCircle, CheckCircle, UserPlus } from 'lucide-react';
import { createUserByAdmin } from '@/api/userService';
import { type UserCreateAdminPayload, type UserRole, type UserStatus } from '@/types';

const CreateUserPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<UserRole>('user');
  const [status, setStatus] = useState<UserStatus>('pending_verification');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 8) {
        setError("Password must be at least 8 characters long.");
        return;
    }
    setIsSubmitting(true);

    const payload: UserCreateAdminPayload = {
      email,
      password,
      name: name || null,
      role,
      status,
    };

    try {
      const newUser = await createUserByAdmin(payload);
      setSuccessMessage(`User "${newUser.email}" created successfully! Redirecting...`);
      setTimeout(() => {
        navigate('/admin/users');
      }, 2000);
    } catch (err: any) {
      console.error("Failed to create user:", err);
      if (err.response && err.response.data && err.response.data.detail) {
         setError(typeof err.response.data.detail === 'string' ? err.response.data.detail : JSON.stringify(err.response.data.detail));
      } else {
        setError("Failed to create user. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 md:py-12">
      <div className="mb-6">
        <Button variant="outline" onClick={() => navigate(-1)} className="text-primary hover:text-primary/80">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Manage Users
        </Button>
      </div>

      <Card className="max-w-2xl mx-auto bg-card border-border/30 shadow-lg">
        <CardHeader>
          <div className="flex items-center gap-3">
            <UserPlus className="h-8 w-8 text-primary" />
            <CardTitle className="text-2xl md:text-3xl text-foreground">Create New User</CardTitle>
          </div>
          <CardDescription className="text-muted-foreground pt-1">Fill in the details to create a new user account.</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-6">
            {error && (
              <div className="p-3 bg-destructive/10 text-destructive border border-destructive/30 rounded-md flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" /> {error}
              </div>
            )}
            {successMessage && (
              <div className="p-3 bg-green-500/10 text-green-700 dark:text-green-400 border border-green-500/30 rounded-md flex items-center">
                <CheckCircle className="h-5 w-5 mr-2" /> {successMessage}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                    <Label htmlFor="email" className="text-foreground">Email Address <span className="text-destructive">*</span></Label>
                    <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" required disabled={isSubmitting} className="!bg-input border-border focus:border-primary focus:ring-primary" />
                </div>
                 <div className="space-y-2">
                    <Label htmlFor="name" className="text-foreground">Full Name (Optional)</Label>
                    <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="John Doe" disabled={isSubmitting} className="!bg-input border-border focus:border-primary focus:ring-primary" />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                    <Label htmlFor="password">Password <span className="text-destructive">*</span></Label>
                    <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min. 8 characters" required disabled={isSubmitting} className="!bg-input border-border focus:border-primary focus:ring-primary" />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password <span className="text-destructive">*</span></Label>
                    <Input id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter password" required disabled={isSubmitting} className="!bg-input border-border focus:border-primary focus:ring-primary" />
                </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                    <Label htmlFor="role" className="text-foreground">Role</Label>
                    <Select value={role} onValueChange={(value: UserRole) => setRole(value)} disabled={isSubmitting}>
                        <SelectTrigger className="!bg-input border-border focus:border-primary focus:ring-primary">
                        <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent className="bg-popover border-border">
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                 <div className="space-y-2">
                    <Label htmlFor="status" className="text-foreground">Status</Label>
                    <Select value={status} onValueChange={(value: UserStatus) => setStatus(value)} disabled={isSubmitting}>
                        <SelectTrigger className="!bg-input border-border focus:border-primary focus:ring-primary">
                        <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent className="bg-popover border-border">
                        <SelectItem value="pending_verification">Pending Verification</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="suspended">Suspended</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

          </CardContent>
          <CardFooter className="border-t border-border/30 pt-6">
            <Button type="submit" className="w-full md:w-auto !bg-gradient-to-r !from-primary !to-accent hover:!from-primary/90 hover:!to-accent/90 text-primary-foreground" disabled={isSubmitting}>
              {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating User...</> : <><Save className="mr-2 h-4 w-4" /> Create User</>}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default CreateUserPage;